import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";
import {
  fundExceptionsApi,
  type FundExceptionItem,
  type FundExceptionListQuery,
  type FundExceptionStatus,
  type FundExceptionType,
} from "../../api/fund-exceptions.api";
import { useHasPermission } from "../../auth/HasPermission";
import { orgCampusApi, type CampusItem } from "../../api/campuses.api";
import { studentsApi, type StudentListItem } from "../../api/students.api";
import { MoneyDisplay } from "../../components/MoneyDisplay";
import { FundExceptionDetailDrawer } from "./FundExceptionDetailDrawer";
import {
  entitlementStatusLabel,
  EXCEPTION_TYPE_LABEL,
  exceptionTypeLabel,
  formatDateTime,
  localStatusLabel,
  STATUS_META,
} from "./fund-exception-labels";

const { RangePicker } = DatePicker;
const { Title } = Typography;

/** ADM-14 资金异常：差错单筛选、只读列表与详情入口。 */
export function FundExceptionPage() {
  const canResolve = useHasPermission("ADM-14", "EDIT");
  const [rows, setRows] = useState<FundExceptionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<FundExceptionListQuery>({});
  const [campuses, setCampuses] = useState<CampusItem[]>([]);
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [activeExceptionId, setActiveExceptionId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    Promise.all([
      orgCampusApi.listCampuses({ page: 1, pageSize: 100 }),
      studentsApi.listStudents({ page: 1, pageSize: 100 }),
    ])
      .then(([campusResult, studentResult]) => {
        setCampuses(campusResult.items);
        setStudents(studentResult.items);
      })
      .catch(() => message.error("筛选项加载失败，请刷新重试"));
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fundExceptionsApi.listFundExceptions({
        ...filters,
        page,
        pageSize,
      });
      setRows(result.items);
      setTotal(result.total);
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "资金异常列表加载失败",
      );
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const updateFilters = (patch: Partial<FundExceptionListQuery>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  const dateRange: [Dayjs, Dayjs] | null =
    filters.discoveredFrom && filters.discoveredTo
      ? [dayjs(filters.discoveredFrom), dayjs(filters.discoveredTo)]
      : null;

  const columns = useMemo<ColumnsType<FundExceptionItem>>(
    () => [
      {
        title: "异常编号",
        dataIndex: "exceptionNo",
        width: 160,
        fixed: "left",
        render: (value: string, record) => (
          <Button
            type="link"
            size="small"
            onClick={() => setActiveExceptionId(record.exceptionId)}
            data-testid={`fund-exception-link-${record.exceptionId}`}
          >
            {value}
          </Button>
        ),
      },
      {
        title: "类型",
        dataIndex: "exceptionType",
        width: 120,
        render: (value: FundExceptionType) => exceptionTypeLabel(value),
      },
      {
        title: "订单号",
        dataIndex: "orderNo",
        width: 150,
        render: (value) => value ?? "-",
      },
      {
        title: "学生",
        dataIndex: "studentName",
        width: 100,
        render: (value) => value ?? "-",
      },
      {
        title: "渠道支付状态",
        dataIndex: "channelStatus",
        width: 120,
        render: (value: string) => localStatusLabel(value),
      },
      {
        title: "本地支付状态",
        dataIndex: "localStatus",
        width: 120,
        render: (value: string) => localStatusLabel(value),
      },
      {
        title: "课时开通状态",
        key: "entitlementStatus",
        width: 120,
        render: (_, record) => entitlementStatusLabel(record),
      },
      {
        title: "异常金额",
        dataIndex: "differenceAmountMinor",
        width: 110,
        render: (value: number) => <MoneyDisplay amountInCents={value} />,
      },
      {
        title: "发现时间",
        dataIndex: "createdAt",
        width: 150,
        render: (value: string) => formatDateTime(value),
      },
      {
        title: "处理状态",
        dataIndex: "status",
        width: canResolve ? 110 : 170,
        fixed: "right",
        render: (value: FundExceptionStatus) => (
          <Space size={4} wrap>
            <Tag color={STATUS_META[value]?.color}>
              {STATUS_META[value]?.label ?? value}
            </Tag>
            {!canResolve && (
              <Tag data-testid="fund-exception-no-permission">无权限</Tag>
            )}
          </Space>
        ),
      },
    ],
    [canResolve],
  );

  return (
    <div>
      <div className="page-header-row">
        <div>
          <Title level={3}>资金异常</Title>
          <p className="page-header-desc">
            核对渠道与本地支付证据，并按审计留痕处理资金差错。
          </p>
        </div>
      </div>
      <Card className="page-filter-card">
        <Space wrap size={12}>
          <Select
            allowClear
            placeholder="异常类型"
            aria-label="异常类型"
            style={{ width: 150 }}
            value={filters.exceptionType}
            options={(
              Object.keys(EXCEPTION_TYPE_LABEL) as FundExceptionType[]
            ).map((value) => ({
              value,
              label: EXCEPTION_TYPE_LABEL[value],
            }))}
            onChange={(exceptionType) => updateFilters({ exceptionType })}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="校区"
            aria-label="校区"
            style={{ width: 150 }}
            value={filters.campusId}
            options={campuses.map((campus) => ({
              value: campus.id,
              label: campus.name,
            }))}
            onChange={(campusId) => updateFilters({ campusId })}
          />
          <Input
            allowClear
            placeholder="订单号"
            aria-label="订单号"
            style={{ width: 170 }}
            value={filters.orderNo}
            onChange={(event) => updateFilters({ orderNo: event.target.value })}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="学生"
            aria-label="学生"
            style={{ width: 170 }}
            value={filters.studentId}
            options={students.map((student) => ({
              value: student.id,
              label: `${student.name}（${student.studentNo}）`,
            }))}
            onChange={(studentId) => updateFilters({ studentId })}
          />
          <Select
            allowClear
            placeholder="处理状态"
            aria-label="处理状态"
            style={{ width: 130 }}
            value={filters.status}
            options={Object.entries(STATUS_META).map(([value, meta]) => ({
              value,
              label: meta.label,
            }))}
            onChange={(status) => updateFilters({ status })}
          />
          <RangePicker
            value={dateRange}
            placeholder={["发现开始时间", "发现结束时间"]}
            onChange={(value) =>
              updateFilters({
                discoveredFrom: value?.[0]?.toISOString(),
                discoveredTo: value?.[1]?.toISOString(),
              })
            }
          />
          <Button
            onClick={() => {
              setFilters({});
              setPage(1);
            }}
          >
            重置
          </Button>
        </Space>
      </Card>
      <Card className="page-content-card">
        <Table
          rowKey="exceptionId"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 1260 }}
          data-testid="fund-exception-table"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (count) => `共 ${count} 条`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />
      </Card>
      {activeExceptionId && (
        <FundExceptionDetailDrawer
          exceptionId={activeExceptionId}
          canResolve={canResolve}
          onClose={() => setActiveExceptionId(null)}
          onChanged={loadList}
        />
      )}
    </div>
  );
}
